import type { Dispatch, UnknownAction } from '@reduxjs/toolkit'

import { feedbackCleared, FEEDBACK_SCOPES } from '../../../shared/model/feedbackSlice'
import { newCommandIntent } from '../../../shared/model/ids'
import { ridKey } from '../../../shared/model/keys'
import { sameId } from '../../../shared/api/wire'
import { journalSyncRequested } from '../../receipts/model/commands'
import {
  cartDiscardRequested,
  cartRetryRequested,
  heldCartDropRequested,
  heldCartRestoreRequested,
  lookupCancelRequested,
  operationResolveCheckRequested,
  operationResolveRequested,
  saleBarcodeScanned,
  saleCancelRequested,
  saleDeliveryRequested,
  salePreparationCancelRequested,
  salePaymentCancelRequested,
  saleFiscalizeRequested,
  saleReceiptRetryRequested,
  saleReceiptRecoverRequested,
  saleAqsiStateCheckRequested,
  salePaymentNotTakenRequested,
  saleParkRequested,
  saleLatePaymentRequested,
  saleHoldRequested,
  saleIssuedOutsideRequested,
  salePayRequested,
  salePayWithRequested,
  salePaymentKindRequested,
  saleReconcileRequested,
  saleReloadRequested,
  saleSearchSubmitted,
  saleStatusCheckRequested,
} from './commands'
import {
  stepQuantity,
  type PaymentResolveActions,
  type PaymentVerdictActions,
  type PaymentVerdictVm,
  type RecoveryPanelActions,
} from '@krp/sale-ui'
import {
  paymentAttemptStarted,
  paymentClosed,
  paymentCollapsed,
  paymentExpanded,
  paymentFeedbackSet,
  paymentResolveClosed,
  paymentResolveOpened,
} from './paymentAttemptsSlice'
import {
  cartEdited,
  deliveryDismissed,
  deliveryOffered,
  lineSelected,
  newSaleStarted,
  outsideChanged,
  outsideClosed,
  outsideOpened,
  pickerPageChanged,
  pickerSearchChanged,
  resolveDraftChanged,
  retryChanged,
  parkClosed,
  parkOpened,
  parkReasonChanged,
  retryClosed,
  retryOpened,
  searchTextChanged,
} from './saleSlice'
import { DELIVERY_NOTICE_KEY, isReceiptRetryChecked, receiptRetryCheckedText } from './selectors'
import { VERDICT_RECEIPT_REASON, VERDICT_RECOVER_CHECKED, verdictReceiptChecked } from './receiptVerdict'
import type {
  AqsiSale,
  AqsiSaleOperation,
  AqsiSaleVm,
  CartBase,
  ParkActions,
  PickerRowVm,
  ReceiptRetryActions,
  ResolveActions,
  ResolveVm,
  SaleActions,
  ScannerActions,
} from './types'

/** Переходы окна продажи — их даёт binding (окна — забота оболочки, не пакета). */
export interface SaleNavigation {
  openScanner: () => void
  /** Окно возврата по исходному чеку; binding без него — кнопки «Открыть возврат» нет. */
  openRefund?: (receiptId: string) => void
  /** Список отложенных продаж (вкладка «Чеки»); binding без него — ссылки в заметке нет. */
  openParked?: () => void
}

/**
 * Действия экрана продажи → команды. Версии, ключи товаров и позиции берутся
 * из vm — из того, что человек ВИДИТ: служба отвергнет команду по устаревшей
 * версии (409), и человек увидит серверную корзину, а не тихую перезапись.
 */
export function createSaleActions(vm: AqsiSaleVm, dispatch: Dispatch<UnknownAction>, navigation: SaleNavigation): SaleActions {
  const resourceId = vm.resourceId
  const seat = vm.seat
  const ref = seat === null ? null : { resourceId, registerKey: seat.registerKey, deviceId: seat.deviceId }
  const base = (): CartBase => ({
    saleId: vm.saleId,
    version: vm.saleVersion ?? 0,
    contact: vm.customerContact === '' ? null : vm.customerContact,
    lines: vm.lines.map((line) => ({
      productKey: line.productKey,
      name: line.name,
      barcode: line.barcode,
      quantity: line.quantity,
      isWeight: line.isWeight,
      unit: line.unit,
      price: line.price,
    })),
  })
  const editable = ref !== null && !vm.cartLocked
  const lineOf = (key: string) => vm.lines.find((line) => line.key === key)
  // Попытка оплаты этой продажи: нажатия кассира окна оплаты (RP-PAY-ONE).
  const attemptRef = () => (ref === null || vm.saleId === null ? null : { ...ref, saleId: vm.saleId, kind: vm.paymentKind })
  const saleCommand = () =>
    ref === null || vm.saleId === null || vm.saleVersion === null
      ? null
      : { ...ref, saleId: vm.saleId, expectedVersion: vm.saleVersion }

  const addRow = (row: PickerRowVm | undefined) => {
    if (ref === null || !editable || row === undefined || !row.available) return
    dispatch(
      cartEdited({
        ...ref,
        base: base(),
        edit: {
          type: 'add',
          line: { productKey: row.productKey, name: row.name, barcode: null, quantity: '1', isWeight: row.isWeight, unit: row.unit },
        },
      }),
    )
  }
  return {
    reload: () => {
      if (ref !== null) dispatch(saleReloadRequested(ref))
    },
    addGood: (productKey) => addRow(vm.picker.rows.find((item) => item.productKey === productKey)),
    addRow: (row) => {
      if (ref !== null && row.absent != null) {
        // Товара нет в кассе: в чек его не положить, но и «не найдено» не сказать — причина дословно и кнопка «Отправить в кассу сейчас».
        dispatch(
          deliveryOffered({
            ...ref,
            offer: { productId: row.absent.productId, name: row.name, reason: row.absent.reason, barcode: row.barcode, text: vm.search.text.trim(), command: 'sale.search', phase: 'offer' },
          }),
        )
        dispatch(searchTextChanged({ ...ref, text: '' }))
        return
      }
      addRow(row)
      // Выбор из выдачи поиска — как Enter: строка очищается для следующего скана.
      if (ref !== null && editable && row.available) dispatch(searchTextChanged({ ...ref, text: '' }))
    },
    searchTextChanged: (text) => {
      if (ref !== null) dispatch(searchTextChanged({ ...ref, text }))
    },
    noticeAction: (key) => {
      if (ref !== null && key === DELIVERY_NOTICE_KEY) dispatch(saleDeliveryRequested(ref))
    },
    noticeDismiss: (key) => {
      if (ref !== null && key === DELIVERY_NOTICE_KEY) dispatch(deliveryDismissed(ref))
    },
    cancelLookup: (id) => {
      if (ref !== null) dispatch(lookupCancelRequested({ ...ref, id }))
    },
    submitSearch: () => {
      if (ref !== null && vm.search.text.trim() !== '') dispatch(saleSearchSubmitted({ ...ref, text: vm.search.text }))
    },
    selectLine: (key) => {
      if (ref !== null) dispatch(lineSelected({ ...ref, key }))
    },
    contactChanged: (value) => {
      if (ref === null || !vm.canEditContact) return
      dispatch(cartEdited({ ...ref, base: base(), edit: { type: 'contact', contact: value } }))
    },
    payWith: (kind) => {
      const button = vm.payButtons.find((item) => item.kind === kind)
      if (ref === null || button?.enabled !== true) return
      // Окно открывается нажатием, не дожидаясь ни сохранения корзины, ни ответа кассы: «Сохраняем корзину…» (есть несохранённая правка) или
      // «Проверяем связь с кассой…». Продажи может ещё не быть — первая позиция создаётся. «Повторить оплату» идёт сюда же.
      dispatch(
        paymentAttemptStarted({
          ...ref,
          saleId: vm.saleId,
          kind,
          saving: vm.cartState !== 'synced' && vm.cartState !== 'empty',
          // Быстрая оплата: чек предыдущей продажи ещё печатается — окно сразу говорит, чего ждёт.
          ...(vm.receiptAhead === true ? { waitingReceipt: true } : {}),
        }),
      )
      dispatch(salePayWithRequested({ ...ref, kind }))
    },
    cancelPreparation: () => {
      if (ref !== null && vm.payment?.canCancelPreparation === true) dispatch(salePreparationCancelRequested(ref))
    },
    hold: () => {
      const command = saleCommand()
      if (command !== null && vm.canHold) dispatch(saleHoldRequested(command))
    },
    restoreHeld: (heldCartId) => {
      if (ref === null) return
      // Чек в работе с позициями откладывается сам — чеки меняются местами, как в старом РМК.
      const current = vm.canHold && vm.saleId !== null && vm.saleVersion !== null ? { saleId: vm.saleId, version: vm.saleVersion } : null
      dispatch(heldCartRestoreRequested({ ...ref, heldCartId, current }))
    },
    dropHeld: (heldCartId) => {
      const cart = vm.held.carts.find((item) => item.id === heldCartId)
      if (ref !== null && cart !== undefined) dispatch(heldCartDropRequested({ ...ref, heldCartId, version: cart.version }))
    },
    clear: () => {
      const command = saleCommand()
      if (command !== null && vm.canClear) dispatch(saleCancelRequested(command))
    },
    setQuantity: (key, quantity) => {
      const line = lineOf(key)
      if (ref === null || !editable || line === undefined) return
      dispatch(
        cartEdited({
          ...ref,
          base: base(),
          edit: { type: 'quantity', productKey: line.productKey, barcode: line.barcode, quantity: quantity.slice(0, 12) },
        }),
      )
    },
    increment: (key) => {
      const line = lineOf(key)
      const next = line === undefined ? null : stepQuantity(line.quantity, 1)
      if (ref === null || !editable || line === undefined || next === null) return
      dispatch(
        cartEdited({
          ...ref,
          base: base(),
          edit: { type: 'quantity', productKey: line.productKey, barcode: line.barcode, quantity: next },
        }),
      )
    },
    decrement: (key) => {
      const line = lineOf(key)
      const next = line === undefined ? null : stepQuantity(line.quantity, -1)
      if (ref === null || !editable || line === undefined || next === null) return
      dispatch(
        cartEdited({
          ...ref,
          base: base(),
          edit: { type: 'quantity', productKey: line.productKey, barcode: line.barcode, quantity: next },
        }),
      )
    },
    setPrice: (key, price) => {
      const line = lineOf(key)
      if (ref === null || !editable || !vm.canEditPrice || line === undefined) return
      dispatch(
        cartEdited({
          ...ref,
          base: base(),
          // Стёртое поле — цена кабинета: пустой цены у позиции не бывает.
          edit: { type: 'price', productKey: line.productKey, barcode: line.barcode, price: price.trim() === '' ? null : price.slice(0, 16) },
        }),
      )
    },
    resetPrice: (key) => {
      const line = lineOf(key)
      if (ref === null || !editable || line === undefined || line.price === null) return
      dispatch(
        cartEdited({ ...ref, base: base(), edit: { type: 'price', productKey: line.productKey, barcode: line.barcode, price: null } }),
      )
    },
    removeLine: (key) => {
      const line = lineOf(key)
      if (ref === null || !editable || line === undefined) return
      dispatch(
        cartEdited({ ...ref, base: base(), edit: { type: 'remove', productKey: line.productKey, barcode: line.barcode } }),
      )
    },
    discardDraft: () => {
      // Отвергнутую службой правку и правку, которая не сохраняется без связи, кассир может оставить: корзина станет как на сервере.
      if (ref !== null && (vm.cartState === 'rejected' || vm.cartState === 'retrying')) dispatch(cartDiscardRequested(ref))
    },
    retryCartSave: () => {
      if (ref !== null && vm.cartState === 'retrying') dispatch(cartRetryRequested(ref))
    },
    choosePaymentKind: (kind) => {
      if (ref === null || !vm.canChoosePaymentKind || kind === vm.paymentKind) return
      dispatch(
        salePaymentKindRequested({
          ...ref,
          kind,
          sale:
            vm.saleId === null || vm.saleVersion === null
              ? null
              : {
                  id: vm.saleId,
                  version: vm.saleVersion,
                  lines: vm.lines.map((line) => ({
                    productKey: line.productKey,
                    barcode: line.barcode,
                    quantity: line.quantity,
                    price: line.price,
                  })),
                },
        }),
      )
    },
    pay: () => {
      const command = saleCommand()
      if (command !== null && vm.canPay) dispatch(salePayRequested(command))
    },
    fiscalize: () => {
      const command = saleCommand()
      if (command !== null && vm.canFiscalize) dispatch(saleFiscalizeRequested(command))
    },
    cancelSale: () => {
      const command = saleCommand()
      if (command !== null && vm.canCancel) dispatch(saleCancelRequested(command))
    },
    cancelPayment: () => {
      const command = saleCommand()
      // Отмена кассиром — тихая: попытку помечает сама команда (listener), отказ окна «Не оплачено» не открывает.
      if (command !== null && vm.payment?.canCancelPayment === true) dispatch(salePaymentCancelRequested(command))
    },
    resolveLatePayment: (paid) => {
      const command = saleCommand()
      const late = vm.payment?.latePayment ?? vm.latePayment ?? null
      if (command !== null && late?.canAct === true) dispatch(saleLatePaymentRequested({ ...command, paid }))
    },
    checkStatus: () => {
      if (ref !== null) dispatch(saleStatusCheckRequested({ ...ref, operationId: vm.operation?.operationId ?? null }))
    },
    reconcile: () => {
      const operation = vm.operation
      if (ref === null || operation === null || !operation.canReconcile || operation.version === null) return
      dispatch(
        saleReconcileRequested({
          ...ref,
          operationId: operation.operationId,
          expectedVersion: operation.version,
          automatic: false,
          saleId: vm.saleId,
        }),
      )
    },
    openManualResolve: () => {
      const attempt = attemptRef()
      // Ручная сверка — этап окна оплаты, а не отдельное окно (решение владельца 23.10.2026, вопрос 2).
      if (attempt !== null && vm.operation?.manualRequired === true) dispatch(paymentResolveOpened(attempt))
    },
    resolve: createPaymentResolveActions(vm, dispatch),
    collapsePayment: () => {
      const attempt = attemptRef()
      if (attempt !== null && vm.payment?.canCollapse === true) dispatch(paymentCollapsed(attempt))
    },
    expandPayment: () => {
      const attempt = attemptRef()
      if (attempt !== null) dispatch(paymentExpanded(attempt))
    },
    closePayment: () => {
      if (ref !== null && vm.payment?.canClose === true) dispatch(paymentClosed(ref))
    },
    startNewSale: () => {
      if (ref !== null) dispatch(newSaleStarted(ref))
    },
    searchChanged: (query) => {
      if (ref !== null) dispatch(pickerSearchChanged({ ...ref, query }))
    },
    pageChanged: (page) => {
      if (ref !== null) dispatch(pickerPageChanged({ ...ref, page }))
    },
    openScanner: navigation.openScanner,
    ...(navigation.openRefund !== undefined && vm.activeRefund !== null
      ? { openRefund: openRefundOf(navigation.openRefund, vm.activeRefund.receiptId) }
      : {}),
    dismissFeedback: () => {
      if (ref !== null) dispatch(feedbackCleared({ key: ridKey(resourceId, FEEDBACK_SCOPES.sale(ref.registerKey)) }))
    },
    openIssuedOutside: () => {
      if (ref !== null && vm.saleId !== null && vm.canMarkIssuedOutside) dispatch(outsideOpened({ ...ref, saleId: vm.saleId }))
    },
    outsideReasonChanged: (reason) => {
      if (ref !== null) dispatch(outsideChanged({ ...ref, reason }))
    },
    outsideReceiptChanged: (receiptId) => {
      if (ref !== null) dispatch(outsideChanged({ ...ref, receiptId }))
    },
    submitIssuedOutside: () => {
      const command = saleCommand()
      const outside = vm.outside
      if (command === null || outside === null || !outside.canSubmit) return
      const receiptId = outside.receiptId.trim()
      dispatch(
        saleIssuedOutsideRequested({
          ...command,
          reason: outside.reason.trim(),
          receiptId: receiptId === '' ? null : receiptId,
        }),
      )
    },
    closeIssuedOutside: () => {
      if (ref !== null) dispatch(outsideClosed(ref))
    },
    receiptRetry: createReceiptRetryActions(vm, dispatch),
    park: createParkActions(vm, dispatch),
    recovery: createRecoveryActions(vm, dispatch),
    ...(navigation.openParked !== undefined ? { openParked: navigation.openParked } : {}),
    syncReceiptJournal: () => {
      // Один ключ на нажатие: повтор той же команды служба узнаёт по нему (ADR-11).
      const intent = newCommandIntent()
      dispatch(journalSyncRequested({ resourceId, operationId: intent.operationId, idempotencyKey: intent.idempotencyKey }))
      if (ref === null) return
      // Сверка журнала — шаг «Привязать уже пробитый чек» окна оплаты: итог — в окне, не внизу.
      dispatch(
        paymentFeedbackSet({
          ...ref,
          saleId: vm.saleId,
          feedback: {
            command: 'receipts.journal-sync',
            target: ref.registerKey,
            phase: 'done',
            error: null,
            note: 'Сверка журнала чеков поставлена. Когда она закончится (ход — в окне «Чеки»), повторите «Привязать уже пробитый чек».',
          },
        }),
      )
    },
  }
}

/** Действия «Чека нет — пробить заново» (RP-FB35 K7). Версия продажи — та, что видит человек: служба отвергнет устаревшую (409). */
export function createReceiptRetryActions(vm: AqsiSaleVm, dispatch: Dispatch<UnknownAction>): ReceiptRetryActions {
  const seat = vm.seat
  const ref = seat === null ? null : { resourceId: vm.resourceId, registerKey: seat.registerKey, deviceId: seat.deviceId }
  const retry = vm.retry
  return {
    // «Повторить проведение»: одно нажатие - одна команда с версией продажи; причина и «что проверили» не нужны.
    repeat: () => {
      if (ref === null || vm.saleId === null || vm.saleVersion === null || retry?.canOpen !== true) return
      dispatch(saleReceiptRetryRequested({ ...ref, saleId: vm.saleId, expectedVersion: vm.saleVersion }))
    },
    open: () => {
      if (ref !== null && vm.saleId !== null && retry?.canOpen === true) dispatch(retryOpened({ ...ref, saleId: vm.saleId }))
    },
    checkedChanged: (checked) => {
      if (ref !== null && isReceiptRetryChecked(checked)) dispatch(retryChanged({ ...ref, checked }))
    },
    checkedTextChanged: (checkedText) => {
      if (ref !== null) dispatch(retryChanged({ ...ref, checkedText }))
    },
    reasonChanged: (reason) => {
      if (ref !== null) dispatch(retryChanged({ ...ref, reason }))
    },
    submit: () => {
      const form = retry?.form
      if (ref === null || vm.saleId === null || vm.saleVersion === null || form == null || !form.canSubmit) return
      dispatch(
        saleReceiptRetryRequested({
          ...ref,
          saleId: vm.saleId,
          expectedVersion: vm.saleVersion,
          reason: form.reason.trim(),
          checked: receiptRetryCheckedText(form),
        }),
      )
    },
    close: () => {
      if (ref !== null) dispatch(retryClosed(ref))
    },
  }
}

/**
 * Панель «Проверка в AQSI» (RP-CASH-RECOVERY §4.1, К-129): «Актуализировать состояние» только читает; основное действие уходит командой
 * «Пробить чек» (`receipt/recover`, К-128) с кодом, который служба назвала основным, и с версией продажи, которую видит человек. Служба
 * выполнит его, только если оно всё ещё основное. `needs-human` — две галочки сверки чека (К-128) прямо в панели.
 */
export function createRecoveryActions(vm: AqsiSaleVm, dispatch: Dispatch<UnknownAction>): RecoveryPanelActions {
  const seat = vm.seat
  const recovery = vm.payment?.recovery ?? null
  const operation = vm.operation
  return {
    refresh: () => {
      if (vm.saleId !== null && recovery?.canRefresh === true) dispatch(saleAqsiStateCheckRequested({ resourceId: vm.resourceId, saleId: vm.saleId }))
    },
    runPrimary: () => {
      const action = vm.recoverAction ?? null
      if (seat === null || vm.saleId === null || vm.saleVersion === null || action === null || recovery?.primary?.enabled !== true) return
      dispatch(
        saleReceiptRecoverRequested({
          resourceId: vm.resourceId,
          registerKey: seat.registerKey,
          deviceId: seat.deviceId,
          saleId: vm.saleId,
          expectedVersion: vm.saleVersion,
          // Не черновик сверки: панель ведёт сама служба — после потерянного ответа она перечитывает AQSI, и действие уходит, только если
          // оно и тогда основное. Двойное нажатие гасит набор команд в полёте и занятая кнопка.
          operationId: null,
          inWindow: true,
          action,
        }),
      )
    },
    verdict: createVerdictActions(
      recovery?.verdict,
      operation === null
        ? null
        : {
            resourceId: vm.resourceId,
            operationId: operation.operationId,
            operationVersion: operation.version,
            saleId: vm.saleId,
            saleVersion: vm.saleVersion,
            seat: seat === null ? null : { registerKey: seat.registerKey, deviceId: seat.deviceId },
            inWindow: true,
          },
      dispatch,
    ),
  }
}

/** Действия «Отложить продажу» (RP-FB35 K9). Версия продажи — та, что видит человек: служба отвергнет устаревшую (409). */
export function createParkActions(vm: AqsiSaleVm, dispatch: Dispatch<UnknownAction>): ParkActions {
  const seat = vm.seat
  const ref = seat === null ? null : { resourceId: vm.resourceId, registerKey: seat.registerKey, deviceId: seat.deviceId }
  const park = vm.park
  return {
    open: () => {
      if (ref !== null && vm.saleId !== null && park?.canOpen === true) dispatch(parkOpened({ ...ref, saleId: vm.saleId }))
    },
    reasonChanged: (reason) => {
      if (ref !== null) dispatch(parkReasonChanged({ ...ref, reason }))
    },
    submit: () => {
      const form = park?.form
      if (ref === null || vm.saleId === null || vm.saleVersion === null || form == null || !form.canSubmit) return
      dispatch(
        saleParkRequested({ ...ref, saleId: vm.saleId, expectedVersion: vm.saleVersion, reason: form.reason.trim() === '' ? null : form.reason.trim() }),
      )
    },
    close: () => {
      if (ref !== null) dispatch(parkClosed(ref))
    },
  }
}

/** Что нужно основному действию сверки по галочкам: операция чека, продажа (версия) и касса. */
interface VerdictTarget {
  resourceId: string
  operationId: string
  /** Версия операции чека для записи сверки; `null` — не прочитана. */
  operationVersion: number | null
  saleId: string | null
  saleVersion: number | null
  seat: { registerKey: string; deviceId: number | null } | null
  /** Сверка из окна оплаты: итог — в окне попытки. */
  inWindow: boolean
}

/**
 * Действия сверки операции чека по галочкам (К-128). Слова записи — системы (`receiptVerdict.ts`); основное действие выбирает
 * `verdict.action`: «Пробить чек» — `receipt/recover`, «Записать: чек пробит» — прежняя сверка операции чека «исполнена» с номером,
 * «Деньги не списаны» — сверка операции оплаты «не исполнена». Недоступное действие ничего не шлёт.
 */
export function createVerdictActions(
  verdict: PaymentVerdictVm | null | undefined,
  target: VerdictTarget | null,
  dispatch: Dispatch<UnknownAction>,
): PaymentVerdictActions {
  const patch = (value: { moneyTaken?: boolean; fiscalized?: boolean; resultId?: string }) => {
    if (target !== null) dispatch(resolveDraftChanged({ resourceId: target.resourceId, operationId: target.operationId, patch: value }))
  }
  return {
    // Сняли «деньги» — снимается и «чек»: без денег чек не пробивается.
    moneyChanged: (moneyTaken) => patch(moneyTaken ? { moneyTaken } : { moneyTaken, fiscalized: false }),
    fiscalChanged: (fiscalized) => patch({ fiscalized }),
    receiptNumberChanged: (resultId) => patch({ resultId }),
    checkStatus: () => {
      if (target !== null) dispatch(operationResolveCheckRequested({ resourceId: target.resourceId, operationId: target.operationId }))
    },
    act: () => {
      if (verdict == null || !verdict.canAct || target === null || target.saleId === null) return
      const { resourceId, operationId, saleId, seat } = target
      const windowSeat = target.inWindow && seat !== null ? { seat: { registerKey: seat.registerKey, saleId } } : {}
      switch (verdict.action) {
        case 'recover':
          if (seat === null || seat.deviceId === null || target.saleVersion === null) return
          dispatch(
            saleReceiptRecoverRequested({
              resourceId,
              registerKey: seat.registerKey,
              deviceId: seat.deviceId,
              saleId,
              expectedVersion: target.saleVersion,
              operationId,
              checked: VERDICT_RECOVER_CHECKED,
              inWindow: target.inWindow,
            }),
          )
          return
        case 'record-receipt': {
          const receipt = verdict.receiptNumber.trim()
          if (target.operationVersion === null || receipt === '') return
          dispatch(
            operationResolveRequested({
              resourceId,
              operationId,
              expectedVersion: target.operationVersion,
              outcome: 'Succeeded',
              reason: VERDICT_RECEIPT_REASON,
              checked: verdictReceiptChecked(receipt),
              resultId: receipt,
              ...windowSeat,
            }),
          )
          return
        }
        case 'payment-not-taken':
          dispatch(salePaymentNotTakenRequested({ resourceId, operationId, saleId, ...windowSeat }))
          return
      }
    },
  }
}

/**
 * Ручная сверка внутри окна оплаты: черновик — по операции продажи, версия — из прочитанной операции; итог и отказ — в окне попытки.
 * Нет операции, требующей сверки, — действия ничего не делают. Операция чека сверяется галочками (`verdict`, К-128).
 */
export function createPaymentResolveActions(vm: AqsiSaleVm, dispatch: Dispatch<UnknownAction>): PaymentResolveActions {
  const operation = vm.operation
  const seat = vm.seat
  const ref = operation === null ? null : { resourceId: vm.resourceId, operationId: operation.operationId }
  const patch = (value: Partial<{ outcome: 'Succeeded' | 'Failed'; reason: string; checked: string; resultId: string }>) => {
    if (ref !== null) dispatch(resolveDraftChanged({ ...ref, patch: value }))
  }
  const verdictTarget: VerdictTarget | null =
    operation === null
      ? null
      : {
          resourceId: vm.resourceId,
          operationId: operation.operationId,
          operationVersion: operation.version,
          saleId: vm.saleId,
          saleVersion: vm.saleVersion,
          seat: seat === null ? null : { registerKey: seat.registerKey, deviceId: seat.deviceId },
          inWindow: true,
        }
  return {
    verdict: createVerdictActions(vm.payment?.resolve?.verdict, verdictTarget, dispatch),
    outcomeChanged: (outcome) => patch({ outcome }),
    reasonChanged: (reason) => patch({ reason }),
    checkedChanged: (checked) => patch({ checked }),
    resultIdChanged: (resultId) => patch({ resultId }),
    submit: () => {
      const form = vm.payment?.resolve
      if (ref === null || seat === null || operation?.version == null || form == null || !form.canSubmit || form.outcome === null) return
      dispatch(
        operationResolveRequested({
          ...ref,
          expectedVersion: operation.version,
          outcome: form.outcome,
          reason: form.reason.trim(),
          checked: form.checked.trim(),
          resultId: form.resultId.trim() === '' ? null : form.resultId.trim(),
          seat: { registerKey: seat.registerKey, saleId: vm.saleId },
        }),
      )
    },
    close: () => {
      if (seat !== null) dispatch(paymentResolveClosed({ resourceId: vm.resourceId, registerKey: seat.registerKey }))
    },
  }
}

/**
 * Действия окна ручной сверки. Итог, причина и проверка — из черновика, версия — из прочитанной операции. Операция чека сверяется
 * галочками (К-128): продажа (версия) и касса — из прочитанных продажи и операции.
 */
export function createResolveActions(
  vm: ResolveVm,
  expectedVersion: number | null,
  dispatch: Dispatch<UnknownAction>,
  close: () => void,
  read: { operation: AqsiSaleOperation | null; sale: AqsiSale | null } = { operation: null, sale: null },
): ResolveActions {
  const ref = { resourceId: vm.resourceId, operationId: vm.operationId }
  const { operation, sale } = read
  const sameSale = sale !== null && operation !== null && sameId(sale.id, operation.saleId)
  const verdictTarget: VerdictTarget = {
    ...ref,
    operationVersion: expectedVersion,
    saleId: operation?.saleId ?? null,
    saleVersion: sameSale ? sale.version : null,
    seat: operation === null ? null : { registerKey: operation.registerKey, deviceId: operation.deviceId },
    inWindow: false,
  }
  return {
    verdict: createVerdictActions(vm.verdict, verdictTarget, dispatch),
    outcomeChanged: (outcome) => dispatch(resolveDraftChanged({ ...ref, patch: { outcome } })),
    reasonChanged: (reason) => dispatch(resolveDraftChanged({ ...ref, patch: { reason } })),
    checkedChanged: (checked) => dispatch(resolveDraftChanged({ ...ref, patch: { checked } })),
    resultIdChanged: (resultId) => dispatch(resolveDraftChanged({ ...ref, patch: { resultId } })),
    submit: () => {
      const { outcome, reason, checked, resultId } = vm.draft
      if (!vm.canSubmit || outcome === null || expectedVersion === null) return
      dispatch(
        operationResolveRequested({
          ...ref,
          expectedVersion,
          outcome,
          reason: reason.trim(),
          checked: checked.trim(),
          resultId: resultId.trim() === '' ? null : resultId.trim(),
        }),
      )
    },
    checkStatus: () => dispatch(operationResolveCheckRequested(ref)),
    close,
    dismissFeedback: () =>
      dispatch(feedbackCleared({ key: ridKey(vm.resourceId, FEEDBACK_SCOPES.resolve(vm.operationId)) })),
  }
}

/** Действия окна сканера: код — команда пакета; товар ищет listener в подборе ЭТОГО кабинета. */
export function createScannerActions(
  resourceId: string,
  dispatch: Dispatch<UnknownAction>,
  close: () => void,
): ScannerActions {
  return {
    code: (code) => dispatch(saleBarcodeScanned({ resourceId, code })),
    close,
  }
}

function openRefundOf(open: (receiptId: string) => void, receiptId: string): () => void {
  return () => open(receiptId)
}
